import React, { useEffect, useState, useMemo } from "react";
import axios from "axios";
import "./NewsEvents.css";

const API_BASE = "https://spst.in/myapp/api/uploads";

// =====================================================
// DEBOUNCE HOOK
// =====================================================

function useDebounced(value, delay = 250) {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const t = setTimeout(() => {
      setDebounced(value);
    }, delay);

    return () => clearTimeout(t);
  }, [value, delay]);

  return debounced;
}


// =====================================================
// DEFAULT ROW KEY
// =====================================================

function defaultRowKey(row, idx) {
  return row.id || row.key || idx;
}


// =====================================================
// SEARCHABLE + PAGINATED TENDER LIST
// =====================================================

function SearchablePaginatedTable({
  data = [],
  columns = [],
  initialPageSize = 10,
  searchPlaceholder = "Search tenders...",
  rowKey = defaultRowKey,
}) {

  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounced(query, 200);

  const [pageSize, setPageSize] = useState(initialPageSize);
  const [page, setPage] = useState(0);


  // ===================================================
  // FILTER
  // ===================================================

  const filtered = useMemo(() => {

    if (!debouncedQuery.trim()) {
      return data;
    }

    const q = debouncedQuery.trim().toLowerCase();

    return data.filter((row) =>
      columns.some((col) => {

        const value =
          typeof col.accessor === "function"
            ? col.accessor(row)
            : row[col.accessor];

        return String(value || "")
          .toLowerCase()
          .includes(q);

      })
    );

  }, [data, debouncedQuery, columns]);


  // ===================================================
  // PAGINATION
  // ===================================================

  const pageCount =
    pageSize >= filtered.length
      ? 1
      : Math.max(
          1,
          Math.ceil(filtered.length / pageSize)
        );


  useEffect(() => {

    if (page >= pageCount) {
      setPage(0);
    }

  }, [pageCount, page]);


  const pageData = useMemo(() => {

    const start = page * pageSize;

    return filtered.slice(
      start,
      start + pageSize
    );

  }, [filtered, page, pageSize]);


  function goto(nextPage) {

    const next = Math.max(
      0,
      Math.min(
        pageCount - 1,
        nextPage
      )
    );

    setPage(next);
  }


  // ===================================================
  // OPEN TENDER
  // ===================================================

  function openTender(row) {

    if (!row.file_path) {
      return;
    }

    window.open(
      `https://spst.in/myapp/${row.file_path}`,
      "_blank",
      "noopener,noreferrer"
    );

  }


  // ===================================================
  // PAGINATION NUMBERS
  // ===================================================

  function getPages() {

    const pages = [];

    if (pageCount <= 5) {

      for (let i = 0; i < pageCount; i++) {
        pages.push(i);
      }

      return pages;
    }


    pages.push(0);

    if (page > 2) {
      pages.push("...");
    }


    const start = Math.max(1, page - 1);

    const end = Math.min(
      pageCount - 2,
      page + 1
    );


    for (let i = start; i <= end; i++) {

      if (!pages.includes(i)) {
        pages.push(i);
      }

    }


    if (page < pageCount - 3) {
      pages.push("...");
    }


    pages.push(pageCount - 1);

    return pages;
  }


  // ===================================================
  // RANGE
  // ===================================================

  const startItem =
    filtered.length === 0
      ? 0
      : page * pageSize + 1;

  const endItem =
    Math.min(
      (page + 1) * pageSize,
      filtered.length
    );


  return (

    <section className="tender-section" style={{padding:'50px'}}>

      <div className="tender-container">


        {/* =========================================
            TOP TOOLBAR
        ========================================== */}

        <div className="tender-toolbar">


          {/* SEARCH */}

          <div className="tender-search">

            <span className="tender-search-icon">
              <i className="fa fa-search"></i>
            </span>

            <input
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setPage(0);
              }}
              placeholder={searchPlaceholder}
            />

            {query && (

              <button
                className="tender-search-clear"
                onClick={() => {
                  setQuery("");
                  setPage(0);
                }}
              >
                ×
              </button>

            )}

          </div>


          {/* PAGE SIZE */}

          <div className="tender-page-size">

            <span>
              Show
            </span>

            <select
              value={pageSize}
              onChange={(e) => {

                setPageSize(
                  Number(e.target.value)
                );

                setPage(0);

              }}
            >

              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={30}>30</option>
              <option value={50}>50</option>

            </select>

            <span>
              tenders
            </span>

          </div>

        </div>


        {/* =========================================
            RESULT INFORMATION
        ========================================== */}

        <div className="tender-result-info">

          <div>

            <span className="result-dot"></span>

            Showing{" "}

            <strong>
              {startItem}
            </strong>

            {" "}–{" "}

            <strong>
              {endItem}
            </strong>

            {" "}of{" "}

            <strong>
              {filtered.length}
            </strong>

            {" "}tenders

          </div>


          {query && (

            <div className="search-result-text">

              Search results for:

              <strong>
                {" "}“{query}”
              </strong>

            </div>

          )}

        </div>


        {/* =========================================
            TENDER LIST
        ========================================== */}

        <div className="tender-list">


          {pageData.length === 0 ? (

            <div className="tender-empty">

              <div className="empty-icon">

                <i className="fa fa-folder-open"></i>

              </div>

              <h3>
                No tenders found
              </h3>

              <p>
                Try searching with a different keyword.
              </p>

              {query && (

                <button
                  onClick={() => {
                    setQuery("");
                    setPage(0);
                  }}
                >
                  Clear Search
                </button>

              )}

            </div>

          ) : (

            pageData.map((row, i) => (

              <div
                key={rowKey(row, i)}
                className="tender-card"
                onClick={() => openTender(row)}
              >


                {/* LEFT DOCUMENT ICON */}

                <div className="tender-document">

                  <div className="document-icon">

                    <i className="fa fa-file-text-o"></i>

                  </div>

                  <span className="document-line"></span>

                </div>


                {/* CONTENT */}

                <div className="tender-content">

                  <span className="tender-label">
                    TENDER DOCUMENT
                  </span>

                  <h3>
                    {row.name}
                  </h3>

                  <div className="tender-meta">

                    <span>
                      <i className="fa fa-file-pdf-o"></i>
                      Official Tender
                    </span>

                    <span className="meta-divider">
                      •
                    </span>

                    <span>
                      Click to view document
                    </span>

                  </div>

                </div>


                {/* RIGHT SIDE */}

                <div className="tender-action">

                  <span className="view-text">
                    View
                  </span>

                  <div className="tender-arrow">

                    <i className="fa fa-arrow-right"></i>

                  </div>

                </div>


              </div>

            ))

          )}

        </div>


        {/* =========================================
            PAGINATION
        ========================================== */}

        {filtered.length > 0 && (

          <div className="tender-pagination">


            <button
              className="pagination-prev"
              disabled={page === 0}
              onClick={() => goto(page - 1)}
            >

              <i className="fa fa-angle-left"></i>

              <span>
                Previous
              </span>

            </button>


            <div className="pagination-numbers">

              {getPages().map((item, index) => (

                item === "..." ? (

                  <span
                    key={`dots-${index}`}
                    className="pagination-dots"
                  >
                    ...
                  </span>

                ) : (

                  <button
                    key={item}
                    className={
                      page === item
                        ? "pagination-number active"
                        : "pagination-number"
                    }
                    onClick={() => goto(item)}
                  >
                    {item + 1}
                  </button>

                )

              ))}

            </div>


            <button
              className="pagination-next"
              disabled={page >= pageCount - 1}
              onClick={() => goto(page + 1)}
            >

              <span>
                Next
              </span>

              <i className="fa fa-angle-right"></i>

            </button>


          </div>

        )}

      </div>

    </section>

  );
}


// =====================================================
// NEWS / TENDERS COMPONENT
// =====================================================

function NewsEvents() {

  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);


  useEffect(() => {

    fetchUploads();

  }, []);


  const fetchUploads = async () => {

    try {

      setLoading(true);

      const res = await axios.get(
        `${API_BASE}/get.php`
      );

      setUploads(
        Array.isArray(res.data)
          ? res.data
          : []
      );

    } catch (error) {

      console.error(
        "Error loading tenders:",
        error
      );

      setUploads([]);

    } finally {

      setLoading(false);

    }

  };


  const columns = [

    {
      header: "Name",
      accessor: "name"
    }

  ];


  return (

    <div className="text-black">


      {/* =========================================
          YOUR EXISTING HEADING
      ========================================== */}

      <div className="row banner">

        <div className="bannerinside">

          <div className="headtitle">

            <div className="title-icon">

              <span className="icon-ring">

                <img
                  src="./images/image.png"
                  alt="Tender"
                />

              </span>

            </div>


            <div className="title-content">

              <span className="title-small">
                TEMPLE
              </span>

              <h2 className="texthead">
                Tenders
              </h2>


              <div className="title-divider">

                <span></span>

                <i>✦</i>

                <span></span>

              </div>

            </div>

          </div>

        </div>

      </div>


      {/* =========================================
          TENDER LIST
      ========================================== */}

      {loading ? (

        <div className="tender-loading">

          <div className="tender-spinner"></div>

          <span>
            Loading tenders...
          </span>

        </div>

      ) : (

        <SearchablePaginatedTable
          data={uploads}
          columns={columns}
          initialPageSize={10}
          searchPlaceholder="Search tender documents..."
        />

      )}

    </div>

  );
}


export default NewsEvents;